import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';
import type { PatientFileCategoryColorToken } from '@/lib/patientFileCategories';

export type PatientFileCategoryRow =
  Database['public']['Tables']['patient_file_categories']['Row'];

export const patientFileCategoriesKey = (clinicId: string | null | undefined) =>
  ['patient-file-categories', clinicId ?? null] as const;

export function usePatientFileCategories(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: patientFileCategoriesKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<PatientFileCategoryRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('patient_file_categories')
        .select('*')
        .eq('clinic_id', clinicId)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

export interface PatientFileCategoryInput {
  name: string;
  color_token: PatientFileCategoryColorToken;
}

export function useCreatePatientFileCategory(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PatientFileCategoryInput): Promise<PatientFileCategoryRow> => {
      if (!clinicId) throw new Error('No active clinic');
      const current = queryClient.getQueryData<PatientFileCategoryRow[]>(
        patientFileCategoriesKey(clinicId),
      );
      const nextOrder =
        (current ?? []).reduce((max, row) => Math.max(max, row.sort_order), 0) + 10;
      const { data, error } = await supabase
        .from('patient_file_categories')
        .insert({
          clinic_id: clinicId,
          name: input.name.trim(),
          color_token: input.color_token,
          sort_order: nextOrder,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: patientFileCategoriesKey(clinicId) });
    },
  });
}

export function useUpdatePatientFileCategory(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<PatientFileCategoryInput> & {
        id: string;
        is_archived?: boolean;
        sort_order?: number;
      },
    ): Promise<PatientFileCategoryRow> => {
      const patch: Database['public']['Tables']['patient_file_categories']['Update'] = {};
      if (input.name != null) patch.name = input.name.trim();
      if (input.color_token != null) patch.color_token = input.color_token;
      if (input.is_archived != null) patch.is_archived = input.is_archived;
      if (input.sort_order != null) patch.sort_order = input.sort_order;
      const { data, error } = await supabase
        .from('patient_file_categories')
        .update(patch)
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: patientFileCategoriesKey(clinicId) });
    },
  });
}

export function useReorderPatientFileCategories(clinicId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orders: Array<{ id: string; sort_order: number }>) => {
      for (const row of orders) {
        const { error } = await supabase
          .from('patient_file_categories')
          .update({ sort_order: row.sort_order })
          .eq('id', row.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: patientFileCategoriesKey(clinicId) });
    },
  });
}

export function useSetDocumentsCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      documentIds: string[];
      categoryId: string | null;
    }): Promise<number> => {
      const { data, error } = await supabase.rpc('set_documents_category', {
        p_document_ids: params.documentIds,
        p_category_id: params.categoryId,
      });
      if (error) throw error;
      return data ?? 0;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}
